import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { DataTable } from '../../shared';
import {
  createStockAdjustment,
  createStockOutRecord,
  getStockOutWorksheet,
} from '../../services/api';
import {
  getStockListHeaderSubtitleSx,
  getStockListHeaderTitleSx,
  getStockListPaperSx,
} from '../../utils/stockMobileLayout';
import StockQtyStepper from './StockQtyStepper';
import StockItemLedgerDrawer from './StockItemLedgerDrawer';
import PageToastStack from '../common/PageToastStack';
import usePageToast from '../../hooks/usePageToast';

const STOCK_IN_FOR_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
];

const USED_BY_OPTIONS = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
];

const SEARCH_DEBOUNCE_MS = 350;

const qtyBorderColor = (isDark) => (isDark ? '#64748b' : '#94a3b8');

const formatStockQuantity = (value, unit) => {
  const u = String(unit || '').trim();
  if (value === null || value === undefined || value === '') {
    return u ? `0 ${u}` : '0';
  }
  const num = Number(value);
  let qtyText = String(value);
  if (Number.isFinite(num)) {
    qtyText = Number.isInteger(num)
      ? String(num)
      : String(num).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }
  return u ? `${qtyText} ${u}` : qtyText;
};

const formatTransferQuantity = (value, unit) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) {
    return formatStockQuantity(0, unit);
  }
  const u = String(unit || '').trim();
  let qtyText = Number.isInteger(num)
    ? String(Math.abs(num))
    : String(Math.abs(num)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  const signed = num > 0 ? `+${qtyText}` : `-${qtyText}`;
  return u ? `${signed} ${u}` : signed;
};

const rowKey = (row) => `${row.item_id}-${row.purchase_for}`;

const isPoolTransfer = (row, usedBy) => {
  const source = String(row.purchase_for || '').toLowerCase();
  const dest = String(usedBy || row.purchase_for || '').toLowerCase();
  return Boolean(source && dest && source !== dest);
};

const actionIconButtonSx = (accent, isDark, { muted = false, iconColor = null } = {}) => ({
  width: 40,
  height: 40,
  border: '1px solid',
  borderColor: muted
    ? alpha(isDark ? '#fff' : '#0f172a', 0.22)
    : alpha(accent, 0.55),
  borderRadius: 1.25,
  color: iconColor || (muted ? (isDark ? '#cbd5e1' : '#64748b') : accent),
  backgroundColor: muted
    ? alpha(isDark ? '#fff' : '#0f172a', isDark ? 0.06 : 0.04)
    : alpha(accent, isDark ? 0.2 : 0.12),
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    color: iconColor || accent,
    backgroundColor: muted
      ? alpha(isDark ? '#fff' : '#0f172a', isDark ? 0.1 : 0.07)
      : alpha(accent, isDark ? 0.3 : 0.18),
    borderColor: muted
      ? alpha(isDark ? '#fff' : '#0f172a', 0.35)
      : alpha(accent, 0.85),
    boxShadow: muted
      ? 'none'
      : `0 2px 10px ${alpha(accent, 0.35)}`,
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    opacity: 0.42,
    transform: 'none',
    boxShadow: 'none',
  },
});

const actionIconSx = { fontSize: 20 };

const ADJUST_ACCENT = '#ab47bc';
const adjustIconColor = (isDark) => (isDark ? '#f3e5f5' : '#6a1b9a');

const StockOutSection = ({ isDark }) => {
  const [worksheetRows, setWorksheetRows] = useState([]);
  const [stockInForFilter, setStockInForFilter] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [debouncedItemSearch, setDebouncedItemSearch] = useState('');
  const [worksheetLoading, setWorksheetLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [worksheetPagination, setWorksheetPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [entryDate, setEntryDate] = useState(moment());
  const {
    pageToasts,
    dismissPageToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  } = usePageToast();
  const [ledgerDrawer, setLedgerDrawer] = useState({ open: false, row: null });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedItemSearch(itemSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [itemSearch]);

  useEffect(() => {
    let cancelled = false;

    const loadWorksheet = async () => {
      setWorksheetLoading(true);
      try {
        const res = await getStockOutWorksheet({
          department: stockInForFilter || undefined,
          search: debouncedItemSearch || undefined,
          page: worksheetPagination.page + 1,
          page_size: worksheetPagination.perPage,
        });
        if (cancelled) return;

        const items = Array.isArray(res?.items) ? res.items : [];
        setWorksheetRows(items);
        setWorksheetPagination((prev) => ({
          ...prev,
          totalRecords: Number(res?.pagination?.total_records ?? items.length ?? 0),
        }));
        setHasLoadedOnce(true);
      } catch {
        if (!cancelled) {
          showError('Failed to load stock items.');
          setWorksheetRows([]);
          setHasLoadedOnce(true);
        }
      } finally {
        if (!cancelled) {
          setWorksheetLoading(false);
        }
      }
    };

    loadWorksheet();
    return () => {
      cancelled = true;
    };
  }, [stockInForFilter, debouncedItemSearch, worksheetPagination.page, worksheetPagination.perPage]);

  useEffect(() => {
    setDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      worksheetRows.forEach((row) => {
        const key = rowKey(row);
        if (!next[key]) {
          changed = true;
          next[key] = {
            quantity: '',
            used_by: row.purchase_for || 'hotel',
            reason: '',
          };
        }
      });
      return changed ? next : prev;
    });
  }, [worksheetRows]);

  const handleDraftChange = (key, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const openLedger = (row) => {
    setLedgerDrawer({ open: true, row });
  };

  const closeLedger = () => {
    setLedgerDrawer({ open: false, row: null });
  };

  const reloadWorksheet = useCallback(async () => {
    setWorksheetLoading(true);
    try {
      const res = await getStockOutWorksheet({
        department: stockInForFilter || undefined,
        search: debouncedItemSearch || undefined,
        page: worksheetPagination.page + 1,
        page_size: worksheetPagination.perPage,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setWorksheetRows(items);
      setWorksheetPagination((prev) => ({
        ...prev,
        totalRecords: Number(res?.pagination?.total_records ?? items.length ?? 0),
      }));
    } catch {
      showError('Failed to load stock items.');
    } finally {
      setWorksheetLoading(false);
    }
  }, [stockInForFilter, debouncedItemSearch, worksheetPagination.page, worksheetPagination.perPage]);

  const validateQty = (key) => {
    const draft = drafts[key] || {};
    const qty = Number(draft.quantity);
    if (!qty || qty <= 0) {
      showWarning('Enter a valid quantity.');
      return null;
    }
    return { draft, qty };
  };

  const handleIssueOut = async (row) => {
    const key = rowKey(row);
    const parsed = validateQty(key);
    if (!parsed) return;

    const { draft, qty } = parsed;
    const usedBy = draft.used_by || row.purchase_for || 'hotel';
    if (isPoolTransfer(row, usedBy)) {
      showInfo('Used By differs from Stock In For — use Transfer instead.');
      return;
    }

    setSavingKey(`${key}-issue`);
    try {
      await createStockOutRecord({
        entry_date: entryDate.format('YYYY-MM-DD'),
        item_id: row.item_id,
        quantity: qty,
        department: usedBy,
        stock_in_for: row.purchase_for,
        notes: draft.reason || '',
      });
      showSuccess(`Issued ${qty} for ${row.item_name}.`);
      setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], quantity: '', reason: '' } }));
      await reloadWorksheet();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to record issue.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleTransfer = async (row) => {
    const key = rowKey(row);
    const parsed = validateQty(key);
    if (!parsed) return;

    const { draft, qty } = parsed;
    const usedBy = draft.used_by || row.purchase_for || 'hotel';
    if (!isPoolTransfer(row, usedBy)) {
      showInfo('Used By matches Stock In For — use Issue instead.');
      return;
    }

    setSavingKey(`${key}-transfer`);
    try {
      await createStockOutRecord({
        entry_date: entryDate.format('YYYY-MM-DD'),
        item_id: row.item_id,
        quantity: qty,
        department: usedBy,
        stock_in_for: row.purchase_for,
        notes: draft.reason || '',
      });
      const sourceLabel = row.purchase_for_label || row.purchase_for;
      const usedByLabel = USED_BY_OPTIONS.find((o) => o.value === usedBy)?.label || usedBy;
      showSuccess(`Transferred ${qty} from ${sourceLabel} to ${usedByLabel} for ${row.item_name}.`);
      setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], quantity: '', reason: '' } }));
      await reloadWorksheet();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to record transfer.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleAdjust = async (row) => {
    const key = rowKey(row);
    const draft = drafts[key] || {};
    const reasonText = String(draft.reason || '').trim();

    if (draft.quantity === '' || Number.isNaN(Number(draft.quantity)) || Number(draft.quantity) < 0) {
      showWarning('Enter actual stock quantity in Qty.');
      return;
    }
    if (!reasonText) {
      showWarning('Enter a reason in the Reason column.');
      return;
    }

    setSavingKey(`${key}-adjust`);
    try {
      await createStockAdjustment({
        adjustment_date: entryDate.format('YYYY-MM-DD'),
        item_id: row.item_id,
        purchase_for: row.purchase_for,
        actual_stock: draft.quantity,
        reason: reasonText,
        notes: reasonText,
      });
      showSuccess(`Adjustment saved for ${row.item_name}.`);
      setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], quantity: '', reason: '' } }));
      await reloadWorksheet();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to save adjustment.');
    } finally {
      setSavingKey(null);
    }
  };

  const worksheetColumns = useMemo(() => [
    { field: 'item_name', label: 'Item', sortable: true },
    { field: 'purchase_for_label', label: 'Stock In For', sortable: true },
    {
      field: 'stock_in_quantity',
      label: 'Stock In',
      sortable: true,
      render: (value, row) => formatStockQuantity(value, row.unit),
    },
    {
      field: 'transfer_quantity',
      label: 'Transfer',
      sortable: true,
      render: (value, row) => formatTransferQuantity(value, row.unit),
    },
    {
      field: 'adjustment_quantity',
      label: 'Adjust',
      sortable: true,
      render: (value, row) => formatTransferQuantity(value, row.unit),
    },
    {
      field: 'stock_out_quantity',
      label: 'Issued',
      sortable: true,
      render: (value, row) => formatStockQuantity(value, row.unit),
    },
    {
      field: 'available_quantity',
      label: 'Available',
      sortable: true,
      render: (value, row) => formatStockQuantity(value, row.unit),
    },
    {
      field: 'used_by',
      label: 'Used By',
      sortable: false,
      render: (_, row) => {
        const key = rowKey(row);
        const draft = drafts[key] || {};
        const usedBy = draft.used_by || row.purchase_for || 'hotel';
        const transfer = isPoolTransfer(row, usedBy);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 130 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={usedBy}
                onChange={(e) => handleDraftChange(key, 'used_by', e.target.value)}
              >
                {USED_BY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {transfer && (
              <Chip
                size="small"
                label="Transfer"
                icon={<SwapHorizIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: alpha('#ff9800', 0.14),
                  color: '#ff9800',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#ff9800' },
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'out_qty',
      label: 'Qty',
      sortable: false,
      render: (_, row) => {
        const key = rowKey(row);
        return (
          <Box sx={{ width: 120, minWidth: 110 }}>
            <StockQtyStepper
              value={drafts[key]?.quantity ?? ''}
              onChange={(v) => handleDraftChange(key, 'quantity', v)}
              isDark={isDark}
              borderColor={qtyBorderColor(isDark)}
            />
          </Box>
        );
      },
    },
    {
      field: 'reason',
      label: 'Reason',
      sortable: false,
      render: (_, row) => {
        const key = rowKey(row);
        return (
          <TextField
            size="small"
            value={drafts[key]?.reason ?? ''}
            onChange={(e) => handleDraftChange(key, 'reason', e.target.value)}
            placeholder="Reason / note"
            sx={{ minWidth: 140 }}
          />
        );
      },
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, row) => {
        const key = rowKey(row);
        const draft = drafts[key] || {};
        const usedBy = draft.used_by || row.purchase_for || 'hotel';
        const transfer = isPoolTransfer(row, usedBy);
        const issueSaving = savingKey === `${key}-issue`;
        const transferSaving = savingKey === `${key}-transfer`;
        const adjustSaving = savingKey === `${key}-adjust`;

        return (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'nowrap', alignItems: 'center', minWidth: 136, py: 0.25 }}>
            <Tooltip title="View ledger">
              <IconButton
                onClick={() => openLedger(row)}
                sx={actionIconButtonSx('#94a3b8', isDark, { muted: true })}
                aria-label="View ledger"
              >
                <ReceiptLongIcon sx={actionIconSx} />
              </IconButton>
            </Tooltip>

            {transfer ? (
              <Tooltip title="Transfer between Hotel and Restaurant pools">
                <span>
                  <IconButton
                    color="warning"
                    disabled={Boolean(savingKey)}
                    onClick={() => handleTransfer(row)}
                    sx={actionIconButtonSx('#ff9800', isDark)}
                    aria-label="Transfer stock"
                  >
                    {transferSaving ? <CircularProgress size={20} /> : <SwapHorizIcon sx={actionIconSx} />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Issue stock (consumption)">
                <span>
                  <IconButton
                    color="primary"
                    disabled={Boolean(savingKey)}
                    onClick={() => handleIssueOut(row)}
                    sx={actionIconButtonSx('#4ce1b6', isDark)}
                    aria-label="Issue stock"
                  >
                    {issueSaving ? <CircularProgress size={20} /> : <RemoveShoppingCartIcon sx={actionIconSx} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title="Adjust — enter actual count in Qty and reason in Reason column">
              <span>
                <IconButton
                  disabled={Boolean(savingKey)}
                  onClick={() => handleAdjust(row)}
                  sx={actionIconButtonSx(ADJUST_ACCENT, isDark, { iconColor: adjustIconColor(isDark) })}
                  aria-label="Adjust stock"
                >
                  {adjustSaving ? (
                    <CircularProgress size={20} sx={{ color: adjustIconColor(isDark) }} />
                  ) : (
                    <TuneIcon sx={{ ...actionIconSx, color: adjustIconColor(isDark) }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [drafts, savingKey, isDark]);

  const filterRowCells = useMemo(() => worksheetColumns.map((column) => {
    switch (column.field) {
      case 'item_name':
        return (
          <TextField
            key={column.field}
            placeholder="Search item..."
            size="small"
            value={itemSearch}
            onChange={(e) => {
              setItemSearch(e.target.value);
              setWorksheetPagination((prev) => ({ ...prev, page: 0 }));
            }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%', minWidth: 120 }}
          />
        );
      case 'purchase_for_label':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%', minWidth: 120 }}>
            <Select
              value={stockInForFilter}
              onChange={(e) => {
                setStockInForFilter(e.target.value);
                setWorksheetPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              {STOCK_IN_FOR_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'actions':
        return (
          <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {(itemSearch || stockInForFilter) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setItemSearch('');
                  setStockInForFilter('');
                  setWorksheetPagination((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        );
      default:
        return <div key={column.field} />;
    }
  }), [worksheetColumns, itemSearch, stockInForFilter]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ mt: 2 }}>
        <Paper elevation={0} sx={getStockListPaperSx(isDark)}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box>
              <Typography sx={getStockListHeaderTitleSx(isDark)}>
                <ImportExportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Stock Movement
              </Typography>
              <Typography sx={getStockListHeaderSubtitleSx(isDark)}>
                Issue consumption, transfer between Hotel and Restaurant pools, or correct stock with adjustment.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <DatePicker
                label="Movement date"
                value={entryDate}
                onChange={(d) => d && setEntryDate(d)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              size="small"
              icon={<RemoveShoppingCartIcon />}
              label="Issue — Used By matches Stock In For"
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<SwapHorizIcon />}
              label="Transfer — Used By differs from Stock In For"
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<TuneIcon />}
              label="Adjust — Qty = actual count, Reason = note"
              variant="outlined"
            />
          </Box>

          {hasLoadedOnce && !worksheetLoading && worksheetPagination.totalRecords === 0 ? (
            <Alert severity="info">
              No stock-in items found. Add purchases in Stock In with &quot;Update inventory&quot; enabled.
            </Alert>
          ) : (
            <DataTable
              columns={worksheetColumns}
              data={worksheetRows}
              loading={worksheetLoading}
              skeletonRowCount={worksheetPagination.perPage}
              pagination={worksheetPagination}
              onPageChange={(page) => setWorksheetPagination((prev) => ({ ...prev, page }))}
              onPerPageChange={(perPage) => setWorksheetPagination((prev) => ({ ...prev, perPage, page: 0 }))}
              stickyHeader
              optimizeScroll
              filterRow={filterRowCells}
            />
          )}
        </Paper>

        <PageToastStack toasts={pageToasts} onDismiss={dismissPageToast} />

        <StockItemLedgerDrawer
          open={ledgerDrawer.open}
          onClose={closeLedger}
          itemId={ledgerDrawer.row?.item_id}
          itemName={ledgerDrawer.row?.item_name}
          purchaseFor={ledgerDrawer.row?.purchase_for}
          purchaseForLabel={ledgerDrawer.row?.purchase_for_label}
          isDark={isDark}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default StockOutSection;
